import React from 'react';

// Shared placeholders for "thinking"/loading states
export const THINKING_PLACEHOLDERS = [
  'Analyzing your question',
  'Searching for relevant documents',
  'Reviewing information',
  'Preparing your response',
];

interface AnimatedEllipsisTextProps {
  text?: string;
  options?: string[]; // Array of placeholder options to cycle through
  optionIntervalMs?: number; // How long to show each option (default 3000ms)
  className?: string;
  style?: React.CSSProperties;
  pulse?: boolean; // Whether to apply pulsing animation
}

/**
 * AnimatedEllipsisText
 * Renders a text string with animated pulsing and animated ellipsis (dot cycling).
 * Used for loading states like "Thinking..." or custom placeholders.
 */

const AnimatedEllipsisText: React.FC<AnimatedEllipsisTextProps> = ({
  text,
  options,
  optionIntervalMs = 3000,
  className = '',
  style = {},
  pulse = true,
}) => {
  const [dots, setDots] = React.useState('');
  const [optionIdx, setOptionIdx] = React.useState(0);

  // Animate ellipsis
  React.useEffect(() => {
    let dotCount = 0;
    const interval = setInterval(() => {
      dotCount = (dotCount + 1) % 4;
      setDots('.'.repeat(dotCount));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  // Cycle through options if provided
  React.useEffect(() => {
    if (!options || options.length < 2) return;
    setOptionIdx(0); // Reset to first on mount
    const interval = setInterval(() => {
      setOptionIdx(prev => (prev + 1) % options.length);
    }, optionIntervalMs);
    return () => clearInterval(interval);
  }, [options, optionIntervalMs]);

  const displayText = options && options.length > 0 ? options[optionIdx] : text || '';

  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        animation: pulse ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : undefined,
        ...style,
      }}
    >
      {displayText}
      {dots}
    </span>
  );
};

export default AnimatedEllipsisText;
